import { GrowthBookContext } from '@growthbook/growthbook-react';
import * as plasmicHost from '@plasmicapp/host';
import type { NextRouter } from 'next/router';
import { HeadManagerContext } from 'next/dist/shared/lib/head-manager-context.shared-runtime';
import { RouterContext } from 'next/dist/shared/lib/router-context.shared-runtime';
import { Context, ReactNode, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createKeepAliveShownEmitter, KeepAliveShownContext, KeepAliveShownEmitter } from '@/common/hooks/useKeepAliveShown';

/**
 * Keep-alive for selected pages (pages router).
 *
 * Why (docs/performance.md §۳.۵): Next unmounts the current page on every navigation, so
 * going Back from a doctor profile rebuilds the whole search page from scratch — ~5s of
 * main thread on a mid-range phone. Instead we keep the search page mounted but hidden
 * (`display: none`) while the user is elsewhere, and simply show it again on return:
 * no render, same results, same scroll position. Same idea as tab/feed retention in
 * native apps.
 *
 * While hidden the page must not react to the rest of the app:
 * - its router reads a **snapshot of its own URL/query** (see createKeptRouter), so
 *   `useRouter()`-driven effects (search params, fetching) never see the profile's URL;
 * - it gets an **inert head manager**, so its <Head> tags (title, canonical, …) leave
 *   the document head instead of leaking into the next page.
 * `router.events` subscriptions still reach it (methods are forwarded to the real router).
 *
 * Render is pure (StrictMode / concurrent safe): a render only *reads* what the last commit
 * left behind and derives the next state from it; every write (kept page record, router
 * snapshot, hidden-since time) happens in a layout effect, i.e. only for renders React
 * commits. A render that is replayed or thrown away changes nothing.
 */

/** Route templates (`router.pathname`) that stay alive when navigated away from. */
export const KEEP_ALIVE_ROUTES = new Set(['/s/[[...params]]']);

/**
 * Hidden longer than this → dropped on return and rebuilt fresh.
 *
 * Product decision: 10 minutes. For comparison, before keep-alive a Back within 5 minutes
 * (react-query's default `cacheTime`) also showed cached results without refetching — the
 * search query is `refetchOnMount: false` and client navigations get no server data
 * (`withCSR`). A kept page never lets its query go inactive, so without this cap it would show
 * cached results for as long as it stays hidden.
 */
export const KEEP_ALIVE_MAX_HIDDEN_MS = 10 * 60 * 1000;

/**
 * Devices reporting at most this much RAM (`navigator.deviceMemory`, GB) never keep a page:
 * there the hidden page's memory raises the chance the OS discards the tab, which would turn
 * the saved re-render into a full reload. Unknown (Safari/Firefox) → keep.
 */
export const KEEP_ALIVE_MIN_DEVICE_MEMORY_GB = 2;

export function deviceCanKeepAlive(): boolean {
  if (typeof navigator === 'undefined') return true;
  const memory = (navigator as any).deviceMemory;
  return typeof memory !== 'number' || memory > KEEP_ALIVE_MIN_DEVICE_MEMORY_GB;
}

const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

const SNAPSHOT_KEYS = [
  'pathname',
  'route',
  'query',
  'asPath',
  'basePath',
  'isReady',
  'isFallback',
  'isPreview',
  'isLocaleDomain',
  'locale',
  'locales',
  'defaultLocale',
  'domainLocales',
] as const;

export function snapshotRouter(router: NextRouter): Record<string, unknown> {
  const snapshot: Record<string, unknown> = {};
  for (const key of SNAPSHOT_KEYS) {
    const value = (router as any)[key];
    snapshot[key] = key === 'query' ? { ...(value as object) } : value;
  }
  return snapshot;
}

export type KeptRouterState = {
  /** the public router (useRouter()): object shape and methods */
  live: NextRouter;
  /**
   * where current URL values are read from: Next's Router singleton (`props.router` in _app).
   * NOT the public router — Next builds a new public instance on every render with the URL
   * values *copied* into it, so a pinned public instance keeps its first query forever (every
   * filter tap then replaced the previous filter instead of adding to it). Defaults to `live`.
   */
  current?: NextRouter;
  /** router values of the last *committed* render in which the page was visible */
  snapshot: Record<string, unknown>;
};

const SNAPSHOT_KEY_SET = new Set<string>(SNAPSHOT_KEYS);

/** The kept page is visible exactly when the current route is a keep-alive route. */
const isKeptPageVisible = (router: NextRouter) => KEEP_ALIVE_ROUTES.has(router.pathname);

/**
 * Router given to the kept page. Its identity never changes: switching the context value
 * would re-render every `useRouter()`/`next/link` consumer in the (hidden) page — hundreds
 * of Plasmic nodes, measured at ~10s on CPU x4. Instead it reads live values while visible
 * and the last visible snapshot while hidden. Methods/events always go to the live router.
 * Visibility is derived from the live router, so nothing is flipped during render.
 */
export function createKeptRouter(state: KeptRouterState): NextRouter {
  // Target MUST be the real router (never `{}`): `in`/`Object.keys` go to the target, and
  // next/link picks the pages-router call signature via `'beforePopState' in router` — with
  // an empty target it switched to the app-router signature and every card tap hard-navigated
  // to a 404. The live router instance never changes, so pinning it as target is safe.
  // eslint-disable-next-line no-undef -- ES2015 global; this repo's eslint env predates it
  return new Proxy(state.live, {
    get(_target, prop) {
      const current = state.current ?? state.live;
      if (typeof prop === 'string' && SNAPSHOT_KEY_SET.has(prop)) {
        return !isKeptPageVisible(current) && prop in state.snapshot ? state.snapshot[prop] : Reflect.get(current, prop);
      }
      const value = Reflect.get(state.live, prop);
      return typeof value === 'function' ? value.bind(state.live) : value;
    },
  });
}

/**
 * Context stabilization at the kept-page boundary.
 *
 * Several app-level providers hand out a *new object* on every App render even when nothing
 * changed (Plasmic's GlobalActionsProvider has no memo at all; DataProvider re-wraps on every
 * `data` identity; GrowthBookProvider re-creates `{ growthbook }`). A kept page consumes them
 * everywhere (every Plasmic node), so without this every navigation re-rendered the whole
 * hidden page — measured slower than not keeping it at all.
 *
 * Rule: keep handing out the previous value while its *content* is unchanged. Functions and
 * class instances compare by identity, plain objects/arrays structurally. While hidden the
 * value is frozen outright.
 */

/** Attribute keys that follow the current URL; the kept page's own URL is what matters. */
const URL_ATTRIBUTE_KEYS = new Set(['url', 'host', 'timeZone']); // follow the current URL, or re-set with cosmetic format differences

export function sameContent(a: unknown, b: unknown, depth = 0, path = ''): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;
  if (depth > 6) return false;
  const proto = Object.getPrototypeOf(a);
  if (proto !== Object.getPrototypeOf(b)) return false;
  if (Array.isArray(a)) {
    const bArr = b as unknown[];
    return a.length === bArr.length && a.every((v, i) => sameContent(v, bArr[i], depth + 1, path));
  }
  if (proto !== Object.prototype && proto !== null) return false; // class instances: identity only
  const ao = a as Record<string, unknown>;
  const bo = b as Record<string, unknown>;
  const ignore = path.endsWith('attributes') ? URL_ATTRIBUTE_KEYS : null;
  const keys = Object.keys(ao).concat(Object.keys(bo).filter(key => !(key in ao)));
  for (const key of keys) {
    if (ignore?.has(key)) continue;
    if (!sameContent(ao[key], bo[key], depth + 1, key)) return false;
  }
  return true;
}

/**
 * GrowthBook's context is `{ growthbook }`, re-created on every render around the same
 * instance. Consumers only observe *evaluated flag values*, so that is the fingerprint.
 * Attributes are deliberately not part of it: several places (app, Plasmic side effects)
 * keep re-setting them with cosmetic differences (e.g. `timeZone` formats), which used to
 * slip through and re-render the whole kept page. If an attribute change really flips a
 * flag, the evaluated values differ and the page re-renders — as it should.
 */
function growthbookFingerprint(value: any): string {
  const gb = value?.growthbook;
  if (!gb) return 'none';
  const evaluated: Record<string, unknown> = {};
  for (const key of Object.keys(gb.getFeatures?.() ?? {})) {
    try {
      evaluated[key] = gb.getFeatureValue(key, null);
    } catch {
      evaluated[key] = '!';
    }
  }
  return `${gb.ready}|${JSON.stringify(evaluated)}`;
}

/**
 * GrowthBook state is only meaningful for the page whose URL it carries. Around a
 * navigation the `url` attribute flips between pages (the page being left and _app both
 * re-set it; measured ~450ms, seconds on slow phones), flipping URL-targeted flags. While
 * the attribute is not this page's URL, keep the previous value instead of re-rendering
 * the whole kept page for a transient state.
 */
function growthbookUrlIsCurrent(url: unknown): boolean {
  return typeof window === 'undefined' || typeof url !== 'string' || url === window.location.href;
}

type Stabilized = {
  context: Context<any>;
  /** false → the live value is transient; keep the held one */
  trusted?: (value: any) => boolean;
  /** fingerprint of the value's observable state; default: structural comparison */
  fingerprint?: (value: any) => string;
};

const STABILIZED_CONTEXTS: Stabilized[] = [
  { context: (plasmicHost as any).DataContext, trusted: (v: any) => growthbookUrlIsCurrent(v?.Growthbook?.attributes?.url) },
  { context: (plasmicHost as any).GlobalActionsContext },
  // `{ growthbook }` is re-created on every render and the instance is always the same, so
  // compare what consumers actually observe (readiness, features, non-URL attributes).
  {
    context: GrowthBookContext,
    fingerprint: growthbookFingerprint,
    trusted: (v: any) => growthbookUrlIsCurrent(v?.growthbook?.getAttributes?.().url),
  },
].filter(entry => !!entry.context);

function StableContext({ entry, frozen, children }: { entry: Stabilized; frozen: boolean; children: ReactNode }) {
  const live = useContext(entry.context);
  // State, not a ref: "adjusting state while rendering" is the pattern React supports — a
  // render that is thrown away or replayed cannot leave a half-applied value behind.
  const [held, setHeld] = useState(() => ({ value: live, fingerprint: entry.fingerprint?.(live) }));
  let next = held;
  if (!frozen && held.value !== live && (entry.trusted?.(live) ?? true)) {
    const nextFingerprint = entry.fingerprint?.(live);
    const same = entry.fingerprint
      ? held.value?.growthbook === live?.growthbook && held.fingerprint === nextFingerprint
      : sameContent(held.value, live);
    if (!same) next = { value: live, fingerprint: nextFingerprint };
  }
  if (next !== held) setHeld(next);
  return <entry.context.Provider value={next.value}>{children}</entry.context.Provider>;
}

function StabilizeContexts({ frozen, children }: { frozen: boolean; children: ReactNode }) {
  const tree = STABILIZED_CONTEXTS.reduceRight<ReactNode>(
    (inner, entry) => (
      <StableContext entry={entry} frozen={frozen}>
        {inner}
      </StableContext>
    ),
    children,
  );
  return <>{tree}</>;
}

const sameInputs = (a: unknown, b: unknown) =>
  Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((v, i) => Object.is(v, b[i]));

const inertHeadManager = { mountedInstances: new Set(), updateHead: () => undefined };

type KeptPage = {
  /** what the element was built from; unchanged → the same element is reused */
  pageInputs: unknown;
  route: string;
  asPath: string;
  element: ReactNode;
  hiddenAt: number | null;
  /** bumped to force a fresh mount (expired while hidden) */
  generation: number;
};

function KeepAliveSlot({
  visible,
  returning,
  keptRouter,
  children,
}: {
  visible: boolean;
  /** the render that shows the page again: keep contexts frozen for this one render, so
   * transient app state from the page being left (e.g. its GrowthBook attributes) cannot
   * trigger a full re-render; later renders compare against the pre-hide values as usual */
  returning: boolean;
  keptRouter: NextRouter;
  children: ReactNode;
}) {
  const headManager = useContext(HeadManagerContext);
  const [signals] = useState<KeepAliveShownEmitter>(createKeepAliveShownEmitter);

  // After the page is committed visible again: let it redo what used to run on remount
  // (analytics events that must behave exactly as before — see useKeepAliveShown).
  useEffect(() => {
    if (returning) signals.emit('shown');
  }, [returning, signals]);

  // After the page is committed hidden: let it stop what an unmount used to stop (timers
  // that would otherwise fire on the next page — see useKeepAliveActive).
  useEffect(() => {
    if (!visible) signals.emit('hidden');
  }, [visible, signals]);

  return (
    <RouterContext.Provider value={keptRouter}>
      <KeepAliveShownContext.Provider value={signals}>
        <HeadManagerContext.Provider value={visible ? headManager : (inertHeadManager as any)}>
          <StabilizeContexts frozen={!visible || returning}>
            <div data-keep-alive={visible ? 'visible' : 'hidden'}>
              {children}
            </div>
          </StabilizeContexts>
        </HeadManagerContext.Provider>
      </KeepAliveShownContext.Provider>
    </RouterContext.Provider>
  );
}

export default function KeepAlivePages({
  router,
  renderPage,
  pageInputs,
}: {
  router: NextRouter;
  renderPage: () => ReactNode;
  /**
   * The identity of what renderPage() depends on (Next's page Component + pageProps).
   * _app re-renders for its own reasons (user store, network status, …); rebuilding the
   * page element then re-renders the whole kept page for nothing — which happened right
   * after returning and cost multi-second locks. Same inputs → same element.
   */
  pageInputs?: unknown[];
}) {
  // The kept router must wrap the *public* router (what useRouter() returns: methods are own
  // properties), not _app's `props.router` (the Router class instance: methods on the
  // prototype). Consumers destructure it — useSearchRouting does `{ query, ...router }` — and
  // object rest copies own properties only, so with the class instance `router.push` was gone
  // and every search filter silently threw.
  const publicRouter = useContext(RouterContext) ?? router;
  // Committed state only: written in the layout effect below, never during render.
  const kept = useRef<KeptPage | null>(null);
  const scroll = useRef<{ asPath: string; y: number } | null>(null);
  // One router state + proxy for the component's lifetime (the public router never changes).
  const [routerState] = useState<KeptRouterState>(() => ({ live: publicRouter, current: router, snapshot: snapshotRouter(router) }));
  const [keptRouter] = useState(() => createKeptRouter(routerState));
  const keepable = KEEP_ALIVE_ROUTES.has(router.pathname);

  // Derive what this render shows from the last committed record — no writes here.
  const prev = kept.current;
  let next: KeptPage | null = prev;
  let returning = false;
  let expired = false;
  if (keepable) {
    expired = !!prev?.hiddenAt && Date.now() - prev.hiddenAt > KEEP_ALIVE_MAX_HIDDEN_MS;
    const fresh = !prev || expired;
    // Back to the exact URL it was hidden at: reuse the same element so React bails out and
    // nothing re-renders. Any other case renders normally with the new props/query.
    returning = !fresh && prev.hiddenAt !== null && prev.asPath === router.asPath;
    const reuse = returning || (!fresh && sameInputs(prev.pageInputs, pageInputs) && prev.asPath === router.asPath);
    next = {
      route: router.pathname,
      asPath: router.asPath,
      pageInputs,
      element: reuse && prev ? prev.element : renderPage(),
      hiddenAt: null,
      generation: (prev?.generation ?? 0) + (expired ? 1 : 0),
    };
  } else if (prev && !deviceCanKeepAlive()) {
    next = null; // low-memory device: let the page go, exactly as without keep-alive
  }

  useIsomorphicLayoutEffect(() => {
    if (keepable) {
      kept.current = next;
      routerState.snapshot = snapshotRouter(router);
      if (expired) scroll.current = null; // fresh page starts at the top
    } else if (next) {
      kept.current = next.hiddenAt === null ? { ...next, hiddenAt: Date.now() } : next;
    } else {
      kept.current = null;
      scroll.current = null;
    }
  });

  useEffect(() => {
    const onStart = () => {
      if (KEEP_ALIVE_ROUTES.has(router.pathname)) scroll.current = { asPath: router.asPath, y: window.scrollY };
    };
    // After Next's own scroll handling (it may scroll to top), put the user back where they were.
    const onComplete = () => {
      if (KEEP_ALIVE_ROUTES.has(router.pathname) && scroll.current?.asPath === router.asPath) {
        // Jump straight back, before the first paint of the returning page (routeChangeComplete runs
        // in the commit's microtasks): the user sees the list where they left it, like a native
        // Back. The old smooth scroll (<html> has scroll-smooth) first showed the top of the list
        // and then animated ~1000px, and every animation step fired scroll handlers (analytics
        // scroll maps) that forced layout of the whole page: ~1.5s extra at CPU x4.
        // eslint-disable-next-line no-undef -- DOM lib type; this repo's eslint env predates it
        window.scrollTo({ top: scroll.current.y, behavior: 'instant' as ScrollBehavior });
      }
    };
    router.events.on('routeChangeStart', onStart);
    router.events.on('routeChangeComplete', onComplete);
    return () => {
      router.events.off('routeChangeStart', onStart);
      router.events.off('routeChangeComplete', onComplete);
    };
  }, [router]);

  return (
    <>
      {next && (
        <KeepAliveSlot key={`${next.route}#${next.generation}`} visible={keepable} returning={returning} keptRouter={keptRouter}>
          {next.element}
        </KeepAliveSlot>
      )}
      {!keepable && renderPage()}
    </>
  );
}
